import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { RepairType } from '@shared/definitions/repair';

/**
 * Validation rule interface
 */
export interface ValidationRule {
  field: string;
  type: 'required' | 'email' | 'minLength' | 'maxLength' | 'pattern' | 'min' | 'max' | 'custom';
  value?: string | number | RegExp;
  message?: string;
  customValidator?: (value: Record<string, unknown>) => boolean;
}

/**
 * Validation result interface
 */
export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

/**
 * Validation error interface
 */
export interface ValidationError {
  field: string;
  message: string;
  value?: unknown;
}

/**
 * @domain Core/Services/Shared
 *
 * Shared validation service for consistent data validation across all services.
 *
 * This service provides:
 * - Standardized validation rules and error messages
 * - Common validation patterns for different data types
 * - Entity-specific validation configurations
 * - Consistent error formatting and reporting
 * - Reusable validation logic across services
 *
 * Usage:
 * ```typescript
 * const rules: ValidationRule[] = [
 *   { field: 'email', type: 'required', message: 'Email is required' },
 *   { field: 'email', type: 'email', message: 'Invalid email format' },
 *   { field: 'name', type: 'minLength', value: 2, message: 'Name must be at least 2 characters' }
 * ];
 *
 * const result = this.validationService.validateEntity(data, rules);
 * if (!result.isValid) {
 *   console.error('Validation errors:', result.errors);
 * }
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class ValidationService {

  private readonly translate = inject(TranslateService);

  // ========================================
  // COMMON VALIDATION PATTERNS
  // ========================================

  private readonly EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  private readonly PHONE_PATTERN = /^[+]?[1-9][\d]{0,15}$/;
  private readonly URL_PATTERN = /^https?:\/\/.+/;
  private readonly UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

  // ========================================
  // ENTITY VALIDATION CONFIGURATIONS
  // ========================================

  /**
   * Get validation rules for Property entities
   */
  getPropertyValidationRules(): ValidationRule[] {
    return [
      { field: 'name', type: 'required', message: this.translate.instant('shared.validation.property.nameRequired') },
      { field: 'name', type: 'minLength', value: 2, message: this.translate.instant('shared.validation.property.nameMinLength') },
      { field: 'name', type: 'maxLength', value: 100, message: this.translate.instant('shared.validation.property.nameMaxLength') },
      { field: 'address', type: 'required', message: this.translate.instant('shared.validation.property.addressRequired') },
      { field: 'address', type: 'minLength', value: 5, message: this.translate.instant('shared.validation.property.addressMinLength') },
      { field: 'type', type: 'required', message: this.translate.instant('shared.validation.property.typeRequired') },
      { field: 'ownerUID', type: 'required', message: this.translate.instant('shared.validation.common.ownerUidRequired') }
    ];
  }

  /**
   * Get validation rules for Tenant entities
   */
  getTenantValidationRules(): ValidationRule[] {
    return [
      { field: 'firstName', type: 'required', message: this.translate.instant('shared.validation.tenant.firstNameRequired') },
      { field: 'firstName', type: 'minLength', value: 2, message: this.translate.instant('shared.validation.tenant.firstNameMinLength') },
      { field: 'lastName', type: 'required', message: this.translate.instant('shared.validation.tenant.lastNameRequired') },
      { field: 'lastName', type: 'minLength', value: 2, message: this.translate.instant('shared.validation.tenant.lastNameMinLength') },
      { field: 'email', type: 'email', message: this.translate.instant('shared.validation.common.emailInvalid') },
      { field: 'phone', type: 'pattern', value: this.PHONE_PATTERN, message: this.translate.instant('shared.validation.common.phoneInvalid') },
      { field: 'ownerUID', type: 'required', message: this.translate.instant('shared.validation.common.ownerUidRequired') }
    ];
  }

  /**
   * Get validation rules for Expense entities
   */
  getExpenseValidationRules(): ValidationRule[] {
    return [
      { field: 'propertyUID', type: 'required', message: this.translate.instant('shared.validation.common.propertyUidRequired') },
      { field: 'amount', type: 'required', message: this.translate.instant('shared.validation.expense.amountRequired') },
      { field: 'amount', type: 'min', value: 0, message: this.translate.instant('shared.validation.expense.amountPositive') },
      { field: 'description', type: 'required', message: this.translate.instant('shared.validation.common.descriptionRequired') },
      { field: 'description', type: 'minLength', value: 3, message: this.translate.instant('shared.validation.expense.descriptionMinLength') },
      { field: 'type', type: 'required', message: this.translate.instant('shared.validation.expense.typeRequired') },
      { field: 'date', type: 'required', message: this.translate.instant('shared.validation.common.dateRequired') },
      { field: 'ownerUID', type: 'required', message: this.translate.instant('shared.validation.common.ownerUidRequired') }
    ];
  }

  /**
   * Get validation rules for Repair entities
   */
  getRepairValidationRules(): ValidationRule[] {
    return [
      { field: 'propertyUID', type: 'required', message: this.translate.instant('shared.validation.common.propertyUidRequired') },
      { field: 'title', type: 'required', message: this.translate.instant('shared.validation.repair.titleRequired') },
      { field: 'title', type: 'minLength', value: 3, message: this.translate.instant('shared.validation.repair.titleMinLength') },
      { field: 'description', type: 'required', message: this.translate.instant('shared.validation.common.descriptionRequired') },
      { field: 'description', type: 'minLength', value: 5, message: this.translate.instant('shared.validation.repair.descriptionMinLength') },
      { field: 'type', type: 'required', message: this.translate.instant('shared.validation.repair.typeRequired') },
      { field: 'status', type: 'required', message: this.translate.instant('shared.validation.repair.statusRequired') },
      { field: 'priority', type: 'required', message: this.translate.instant('shared.validation.repair.priorityRequired') },
      { field: 'reportedDate', type: 'required', message: this.translate.instant('shared.validation.repair.reportedDateRequired') },
      { field: 'ownerUID', type: 'required', message: this.translate.instant('shared.validation.common.ownerUidRequired') }
    ];
  }

  /**
   * Get validation rules for Contract entities
   */
  getContractValidationRules(): ValidationRule[] {
    return [
      { field: 'propertyUID', type: 'required', message: this.translate.instant('shared.validation.common.propertyUidRequired') },
      {
        field: 'tenantUID',
        type: 'custom',
        message: this.translate.instant('shared.validation.contract.tenantRequired'),
        customValidator: (entity: Record<string, unknown>) => {
          // Check both legacy tenantUID and new tenantIds array
          const tenantUID = entity['tenantUID'] as string | undefined;
          const tenantIds = entity['tenantIds'] as unknown[] | undefined;
          return (!!tenantUID && tenantUID.trim() !== '') ||
                 (!!tenantIds && Array.isArray(tenantIds) && tenantIds.length > 0);
        }
      },
      {
        field: 'contractDates.startDate',
        type: 'custom',
        message: this.translate.instant('shared.validation.contract.startDateRequired'),
        customValidator: (entity: Record<string, unknown>) => {
          const contractDates = entity['contractDates'] as Record<string, unknown> | undefined;
          return !!contractDates && !!contractDates['startDate'];
        }
      },
      {
        field: 'contractDates.endDate',
        type: 'custom',
        message: this.translate.instant('shared.validation.contract.endDateRequired'),
        customValidator: (entity: Record<string, unknown>) => {
          // End date is optional for indefinite contracts
          if (entity['durationType'] === 'IndefiniteTerm') return true;
          const contractDates = entity['contractDates'] as Record<string, unknown> | undefined;
          return !!contractDates && !!contractDates['endDate'];
        }
      },
      {
        field: 'contractFinancials.baseRentAmount',
        type: 'custom',
        message: this.translate.instant('shared.validation.contract.monthlyRentRequired'),
        customValidator: (entity: Record<string, unknown>) => {
          const contractFinancials = entity['contractFinancials'] as Record<string, unknown> | undefined;
          return !!contractFinancials &&
                 !!contractFinancials['baseRentAmount'] &&
                 (contractFinancials['baseRentAmount'] as number) > 0;
        }
      },
      {
        field: 'contractFinancials.depositAmount',
        type: 'custom',
        message: this.translate.instant('shared.validation.contract.depositPositive'),
        customValidator: (entity: Record<string, unknown>) => {
          const contractFinancials = entity['contractFinancials'] as Record<string, unknown> | undefined;
          if (!contractFinancials) return true; // Let other validation handle missing financials
          return (contractFinancials['depositAmount'] as number) >= 0;
        }
      },
      {
        field: 'workspaceId',
        type: 'required',
        message: this.translate.instant('shared.validation.contract.workspaceIdRequired')
      },
      {
        field: 'contractDates',
        type: 'custom',
        message: this.translate.instant('shared.validation.contract.endDateAfterStart'),
        customValidator: (entity: Record<string, unknown>) => {
          const contractDates = entity['contractDates'] as Record<string, unknown> | undefined;
          if (!contractDates || !contractDates['startDate']) return true;
          // Skip validation for indefinite contracts
          if (entity['durationType'] === 'IndefiniteTerm' || !contractDates['endDate']) return true;
          return new Date(contractDates['endDate'] as string) > new Date(contractDates['startDate'] as string);
        }
      }
    ];
  }

  /**
   * Get validation rules for Note entities
   */
  getNoteValidationRules(): ValidationRule[] {
    return [
      { field: 'description', type: 'required', message: this.translate.instant('shared.validation.note.descriptionRequired') },
      { field: 'description', type: 'minLength', value: 1, message: this.translate.instant('shared.validation.note.descriptionNotEmpty') },
      { field: 'authorUID', type: 'required', message: this.translate.instant('shared.validation.note.authorUidRequired') },
      { field: 'authorName', type: 'required', message: this.translate.instant('shared.validation.note.authorNameRequired') },
      { field: 'date', type: 'required', message: this.translate.instant('shared.validation.common.dateRequired') }
    ];
  }

  // ========================================
  // VALIDATION METHODS
  // ========================================

  /**
   * Validate an entity against a set of rules
   */
  validateEntity(entity: Record<string, unknown>, rules: ValidationRule[]): ValidationResult {
    const errors: ValidationError[] = [];

    for (const rule of rules) {
      const fieldValue = this.getFieldValue(entity, rule.field);
      const error = this.validateField(rule.field, fieldValue, rule, entity);

      if (error) {
        errors.push(error);
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate a single field value
   */
  validateField(fieldName: string, value: unknown, rule: ValidationRule, entity?: Record<string, unknown>): ValidationError | null {
    switch (rule.type) {
      case 'required':
        if (this.isEmpty(value)) {
          return {
            field: fieldName,
            message: rule.message || `${fieldName} is required`,
            value
          };
        }
        break;

      case 'email':
        if (value && typeof value === 'string' && !this.EMAIL_PATTERN.test(value)) {
          return {
            field: fieldName,
            message: rule.message || `${fieldName} must be a valid email`,
            value
          };
        }
        break;

      case 'minLength':
        if (value && typeof value === 'string' && value.length < (rule.value as number)) {
          return {
            field: fieldName,
            message: rule.message || `${fieldName} must be at least ${rule.value} characters`,
            value
          };
        }
        break;

      case 'maxLength':
        if (value && typeof value === 'string' && value.length > (rule.value as number)) {
          return {
            field: fieldName,
            message: rule.message || `${fieldName} cannot exceed ${rule.value} characters`,
            value
          };
        }
        break;

      case 'pattern':
        if (value && typeof value === 'string' && !(rule.value as RegExp).test(value)) {
          return {
            field: fieldName,
            message: rule.message || `${fieldName} format is invalid`,
            value
          };
        }
        break;

      case 'min':
        if (value !== null && value !== undefined && Number(value) < (rule.value as number)) {
          return {
            field: fieldName,
            message: rule.message || `${fieldName} must be at least ${rule.value}`,
            value
          };
        }
        break;

      case 'max':
        if (value !== null && value !== undefined && Number(value) > (rule.value as number)) {
          return {
            field: fieldName,
            message: rule.message || `${fieldName} cannot exceed ${rule.value}`,
            value
          };
        }
        break;

      case 'custom':
        if (rule.customValidator && !rule.customValidator(entity || { [fieldName]: value })) {
          return {
            field: fieldName,
            message: rule.message || `${fieldName} validation failed`,
            value
          };
        }
        break;
    }

    return null;
  }

  /**
   * Validate property-specific business rules
   */
  validateProperty(property: Record<string, unknown>): ValidationResult {
    const rules = this.getPropertyValidationRules();
    const baseResult = this.validateEntity(property, rules);

    // Add property-specific validations
    const additionalErrors: ValidationError[] = [];

    // Validate property type enum
    if (property['type'] && !this.isValidPropertyType(property['type'] as string)) {
      additionalErrors.push({
        field: 'type',
        message: this.translate.instant('shared.validation.property.typeInvalid'),
        value: property['type']
      });
    }

    return {
      isValid: baseResult.isValid && additionalErrors.length === 0,
      errors: [...baseResult.errors, ...additionalErrors]
    };
  }

  /**
   * Validate tenant-specific business rules
   */
  validateTenant(tenant: Record<string, unknown>): ValidationResult {
    const rules = this.getTenantValidationRules();
    const baseResult = this.validateEntity(tenant, rules);

    // Add tenant-specific validations
    const additionalErrors: ValidationError[] = [];

    // Validate tenant type enum
    if (tenant['type'] && !this.isValidTenantType(tenant['type'] as string)) {
      additionalErrors.push({
        field: 'type',
        message: this.translate.instant('shared.validation.tenant.typeInvalid'),
        value: tenant['type']
      });
    }

    return {
      isValid: baseResult.isValid && additionalErrors.length === 0,
      errors: [...baseResult.errors, ...additionalErrors]
    };
  }

  /**
   * Validate expense-specific business rules
   */
  validateExpense(expense: Record<string, unknown>): ValidationResult {
    const rules = this.getExpenseValidationRules();
    const baseResult = this.validateEntity(expense, rules);

    // Add expense-specific validations
    const additionalErrors: ValidationError[] = [];

    // Validate expense type enum
    if (expense['type'] && !this.isValidExpenseType(expense['type'] as string)) {
      additionalErrors.push({
        field: 'type',
        message: this.translate.instant('shared.validation.expense.typeInvalid'),
        value: expense['type']
      });
    }

    // Validate date is not in the future for completed expenses
    if (expense['isPaid'] && expense['paymentDate'] && new Date(expense['paymentDate'] as string) > new Date()) {
      additionalErrors.push({
        field: 'paymentDate',
        message: this.translate.instant('shared.validation.expense.paymentDateNotFuture'),
        value: expense['paymentDate']
      });
    }

    return {
      isValid: baseResult.isValid && additionalErrors.length === 0,
      errors: [...baseResult.errors, ...additionalErrors]
    };
  }

  /**
   * Validate repair-specific business rules
   */
  validateRepair(repair: Record<string, unknown>): ValidationResult {
    const rules = this.getRepairValidationRules();
    const baseResult = this.validateEntity(repair, rules);

    // Add repair-specific validations
    const additionalErrors: ValidationError[] = [];

    // Validate repair type enum
    if (repair['type'] && !this.isValidRepairType(repair['type'] as string)) {
      additionalErrors.push({
        field: 'type',
        message: this.translate.instant('shared.validation.repair.typeInvalid'),
        value: repair['type']
      });
    }

    // Validate completed date logic
    if (repair['status'] === 'completed' && repair['completedDate'] && repair['reportedDate']) {
      if (new Date(repair['completedDate'] as string) < new Date(repair['reportedDate'] as string)) {
        additionalErrors.push({
          field: 'completedDate',
          message: this.translate.instant('shared.validation.repair.completionDateAfterReported'),
          value: repair['completedDate']
        });
      }
    }

    return {
      isValid: baseResult.isValid && additionalErrors.length === 0,
      errors: [...baseResult.errors, ...additionalErrors]
    };
  }

  // ========================================
  // UTILITY METHODS
  // ========================================

  /**
   * Check if a value is empty
   */
  private isEmpty(value: unknown): boolean {
    return value === null ||
           value === undefined ||
           (typeof value === 'string' && value.trim() === '') ||
           (Array.isArray(value) && value.length === 0);
  }

  /**
   * Get field value from object using dot notation
   */
  private getFieldValue(obj: Record<string, unknown>, fieldPath: string): unknown {
    return fieldPath.split('.').reduce<unknown>((current, key) => {
      if (current && typeof current === 'object') {
        return (current as Record<string, unknown>)[key];
      }
      return undefined;
    }, obj);
  }

  /**
   * Validate property type enum (these should match your actual enums)
   */
  private isValidPropertyType(type: string): boolean {
    const validTypes = ['apartment', 'house', 'commercial', 'land', 'other'];
    return validTypes.includes(type.toLowerCase());
  }

  /**
   * Validate tenant type enum (these should match your actual enums)
   */
  private isValidTenantType(type: string): boolean {
    const validTypes = ['private', 'professional', 'company'];
    return validTypes.includes(type.toLowerCase());
  }

  /**
   * Validate expense type enum (these should match your actual enums)
   */
  private isValidExpenseType(type: string): boolean {
    const validTypes = ['maintenance', 'repair', 'utility', 'insurance', 'tax', 'management', 'other'];
    return validTypes.includes(type.toLowerCase());
  }

  /**
   * Validate repair type. Derived from the enum rather than retyped: the hand
   * -copied list silently rejected any value added to `RepairType` (#337).
   */
  private isValidRepairType(type: string): boolean {
    return (Object.values(RepairType) as string[]).includes(type.toLowerCase());
  }

  /**
   * Format validation errors for display
   */
  formatValidationErrors(errors: ValidationError[]): string {
    return errors.map(error => error.message).join(', ');
  }

  /**
   * Get validation errors for a specific field
   */
  getFieldErrors(errors: ValidationError[], fieldName: string): ValidationError[] {
    return errors.filter(error => error.field === fieldName);
  }

  /**
   * Check if a specific field has errors
   */
  hasFieldError(errors: ValidationError[], fieldName: string): boolean {
    return errors.some(error => error.field === fieldName);
  }
}


